import { X, Send, Image, MessageCircle, Reply, Trash2, ThumbsUp, Heart, Laugh, ThumbsDown, Download, FileText, CheckCircle, ArrowRight, Clock, Shield, AlertTriangle, Zap, Paperclip, Bell, ChevronDown, ChevronUp, Eye, ExternalLink } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import config from "../../config";
import Swal from "sweetalert2";

const REACTION_TYPES = {
  Like: { icon: ThumbsUp, color: "text-blue-400", hoverColor: "hover:bg-blue-500/20" },
  Love: { icon: Heart, color: "text-red-400", hoverColor: "hover:bg-red-500/20" },
  Laugh: { icon: Laugh, color: "text-yellow-400", hoverColor: "hover:bg-yellow-500/20" },
  Dislike: { icon: ThumbsDown, color: "text-slate-400", hoverColor: "hover:bg-slate-500/20" },
};

// ─── SLA Section ─────────────────────────────────────────────────────────────
function SLASection({ ticket, currentStatus, apiBaseUrl }) {
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const fetchSLA = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${apiBaseUrl}/SLA/priority/${encodeURIComponent(ticket.priority)}`);
        if (res.ok) {
          const data = await res.json();
          setSlaData(Array.isArray(data) ? data[0] : data || null);
        } else setSlaData(null);
      } catch { setSlaData(null); } finally { setLoading(false); }
    };
    if (ticket.priority) fetchSLA(); else setLoading(false);
  }, [ticket.priority, apiBaseUrl]);

  useEffect(() => {
    const calc = () => !ticket.createdAt ? 0 : Math.floor((new Date() - new Date(ticket.createdAt)) / 60000);
    setElapsed(calc());
    const iv = setInterval(() => setElapsed(calc()), 60000);
    return () => clearInterval(iv);
  }, [ticket.createdAt]);

  const parseToMins = (tg) => {
    if (!tg) return null;
    const m = tg.toLowerCase().trim().match(/^(\d+(?:\.\d+)?)\s*(h|hr|hours?|d|days?|w|weeks?|m|min|minutes?)$/);
    if (!m) return null;
    const v = parseFloat(m[1]), u = m[2];
    if (u.startsWith("h")) return v * 60;
    if (u.startsWith("d")) return v * 1440;
    if (u.startsWith("w")) return v * 10080;
    return v;
  };
  const fmtMins = (m) => m < 60 ? `${Math.round(m)}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;

  const goalMins = slaData ? parseToMins(slaData.timeGoal) : null;
  const isResolved = currentStatus === "Resolved";
  const pct = goalMins ? Math.min((elapsed / goalMins) * 100, 100) : 0;
  const remaining = goalMins ? goalMins - elapsed : null;
  const isBreached = goalMins ? elapsed > goalMins && !isResolved : false;
  const isAtRisk = goalMins ? !isBreached && remaining !== null && remaining < goalMins * 0.2 && !isResolved : false;
  const slaStatus = isResolved ? "resolved" : isBreached ? "breached" : isAtRisk ? "at-risk" : "on-track";

  const cfgMap = {
    "on-track": { label: "On Track", icon: Shield, gradient: "from-emerald-500/20 to-teal-500/10", border: "border-emerald-500/30", iconColor: "text-emerald-400", labelColor: "text-emerald-400", barColor: "from-emerald-500 to-teal-400", pulse: false },
    "at-risk": { label: "At Risk", icon: AlertTriangle, gradient: "from-amber-500/20 to-orange-500/10", border: "border-amber-500/30", iconColor: "text-amber-400", labelColor: "text-amber-400", barColor: "from-amber-500 to-orange-400", pulse: true },
    breached: { label: "SLA Breached", icon: Zap, gradient: "from-red-500/20 to-rose-500/10", border: "border-red-500/30", iconColor: "text-red-400", labelColor: "text-red-400", barColor: "from-red-500 to-rose-400", pulse: true },
    resolved: { label: "Completed", icon: CheckCircle, gradient: "from-blue-500/20 to-indigo-500/10", border: "border-blue-500/30", iconColor: "text-blue-400", labelColor: "text-blue-400", barColor: "from-blue-500 to-indigo-400", pulse: false },
  };
  const cfg = cfgMap[slaStatus];
  const StatusIcon = cfg.icon;

  if (loading) return <div><p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">SLA</p><div className="flex justify-center py-4"><div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div></div>;
  if (!slaData) return <div><p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">SLA</p><div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 text-center"><Shield size={20} className="text-slate-600 mx-auto mb-1.5" /><p className="text-slate-500 text-xs">No SLA for <span className="text-slate-400 font-medium">{ticket.priority}</span></p></div></div>;

  return (
    <div>
      <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">SLA</p>
      <div className={`bg-gradient-to-br ${cfg.gradient} border ${cfg.border} rounded-xl p-3.5 mb-2`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg bg-black/20 ${cfg.pulse ? "animate-pulse" : ""}`}><StatusIcon size={14} className={cfg.iconColor} /></div>
            <span className={`text-xs font-bold ${cfg.labelColor}`}>{cfg.label}</span>
          </div>
          <span className="text-slate-400 text-xs font-mono bg-black/20 px-2 py-0.5 rounded-md">{slaData.timeGoal}</span>
        </div>
        {goalMins && <div className="mb-3"><div className="flex justify-between mb-1.5"><span className="text-slate-400 text-xs">{isResolved ? "Completed in" : "Elapsed"}</span><span className="text-slate-300 text-xs font-mono font-semibold">{fmtMins(elapsed)} / {slaData.timeGoal}</span></div><div className="h-1.5 bg-black/30 rounded-full overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${cfg.barColor} transition-all duration-700`} style={{ width: `${pct}%` }} /></div></div>}
        {goalMins && !isResolved && <div className={`flex items-center gap-2 rounded-lg p-2 ${isBreached ? "bg-red-500/10" : "bg-black/20"}`}><Clock size={12} className={isBreached ? "text-red-400" : "text-slate-400"} /><span className={`text-xs font-medium ${isBreached ? "text-red-400" : "text-slate-300"}`}>{isBreached ? `Overdue by ${fmtMins(Math.abs(remaining))}` : `${fmtMins(remaining)} remaining`}</span></div>}
      </div>
      <div className="space-y-1.5">
        {slaData.calendar && <div className="flex justify-between bg-slate-800/40 border border-slate-700/40 rounded-lg px-3 py-2"><span className="text-slate-500 text-xs">Calendar</span><span className="text-slate-300 text-xs font-medium">{slaData.calendar}</span></div>}
        {slaData.condition && <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg px-3 py-2"><p className="text-slate-500 text-xs mb-1">Condition</p><p className="text-slate-300 text-xs leading-relaxed line-clamp-2">{slaData.condition}</p></div>}
      </div>
    </div>
  );
}

// ─── All Attachments Modal ────────────────────────────────────────────────────
function AllAttachmentsModal({ ticketNumber, onClose, formatDate, apiBaseUrl }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewAtt, setPreviewAtt] = useState(null);

  useEffect(() => {
    fetch(`${apiBaseUrl}/TicketAttachments/${encodeURIComponent(ticketNumber)}`)
      .then(r => r.ok ? r.json() : [])
      .then(setAttachments)
      .catch(() => setAttachments([]))
      .finally(() => setLoading(false));
  }, [ticketNumber]);

  const isImage = (ct, fn) => {
    const ext = (fn || "").split(".").pop()?.toLowerCase();
    return (ct || "").includes("image") || ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext);
  };

  const getFileIcon = (ct, fn) => {
    const ext = (fn || "").split(".").pop()?.toLowerCase();
    if (isImage(ct, fn)) return <span className="text-blue-300 text-xs font-bold">IMG</span>;
    if ((ct || "").includes("pdf") || ext === "pdf") return <span className="text-red-300 text-xs font-bold">PDF</span>;
    if (["doc", "docx"].includes(ext)) return <span className="text-blue-300 text-xs font-bold">DOC</span>;
    if (["xls", "xlsx"].includes(ext)) return <span className="text-emerald-300 text-xs font-bold">XLS</span>;
    return <FileText size={14} className="text-slate-400" />;
  };

  const fmtSize = (b) => !b ? "" : b < 1024 ? `${b}B` : b < 1048576 ? `${(b / 1024).toFixed(1)}KB` : `${(b / 1048576).toFixed(1)}MB`;

  return (
    <div className="fixed inset-0 z-[10999] flex items-center justify-center p-6" style={{ backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }} onClick={e => e.stopPropagation()}>
      <div className="w-full max-w-2xl flex flex-col rounded-2xl overflow-hidden" style={{ background: "linear-gradient(160deg,#0f172a,#1e293b)", border: "1px solid rgba(148,163,184,0.12)", boxShadow: "0 30px 80px rgba(0,0,0,0.9)", maxHeight: "calc(100vh - 80px)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ background: "linear-gradient(135deg,#1e3a5f,#1d4ed8)", borderBottom: "1px solid rgba(59,130,246,0.25)" }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Paperclip size={15} className="text-white" /></div>
            <div>
              <p className="text-white font-bold text-sm">Attachments</p>
              <p className="text-blue-200/60 text-xs">{ticketNumber} · {attachments.length} file{attachments.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><X size={14} className="text-white" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>}

          {!loading && attachments.length === 0 && (
            <div className="text-center py-12">
              <Paperclip size={28} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No attachments found</p>
            </div>
          )}

          {!loading && attachments.length > 0 && (
            <>
              {/* Image Grid */}
              {attachments.filter(a => isImage(a.contentType, a.fileName)).length > 0 && (
                <div className="mb-5">
                  <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold mb-3">Images</p>
                  <div className="grid grid-cols-3 gap-3">
                    {attachments.filter(a => isImage(a.contentType, a.fileName)).map(att => (
                      <div key={att.id} className="group relative rounded-xl overflow-hidden border border-slate-700/50 hover:border-blue-500/50 transition-all" style={{ aspectRatio: "1" }}>
                        <img src={`${apiBaseUrl}/TicketAttachments/file/${att.id}`} alt={att.fileName} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button onClick={() => setPreviewAtt(att)} className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors" title="Preview"><Eye size={14} className="text-white" /></button>
                          <a href={`${apiBaseUrl}/TicketAttachments/file/${att.id}`} download={att.fileName} className="w-8 h-8 rounded-lg bg-blue-500/60 hover:bg-blue-500 flex items-center justify-center transition-colors" title="Download"><Download size={14} className="text-white" /></a>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-xs truncate">{att.fileName}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files List */}
              {attachments.filter(a => !isImage(a.contentType, a.fileName)).length > 0 && (
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold mb-3">Files</p>
                  <div className="space-y-2">
                    {attachments.filter(a => !isImage(a.contentType, a.fileName)).map(att => (
                      <div key={att.id} className="flex items-center gap-3 bg-slate-800/50 hover:bg-slate-800/80 border border-slate-700/40 hover:border-slate-600 rounded-xl px-4 py-3 transition-all group">
                        <div className="w-9 h-9 rounded-lg bg-slate-700/60 border border-slate-600/40 flex items-center justify-center shrink-0">
                          {getFileIcon(att.contentType, att.fileName)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-200 text-sm font-medium truncate">{att.fileName}</p>
                          <p className="text-slate-500 text-xs">{fmtSize(att.fileSize)}{att.uploadedAt && ` · ${formatDate ? formatDate(att.uploadedAt) : att.uploadedAt}`}</p>
                        </div>
                        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a href={`${apiBaseUrl}/TicketAttachments/file/${att.id}`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 flex items-center justify-center transition-colors" title="Open in new tab"><ExternalLink size={13} className="text-slate-300" /></a>
                          <a href={`${apiBaseUrl}/TicketAttachments/file/${att.id}`} download={att.fileName} className="w-8 h-8 rounded-lg bg-blue-600/80 hover:bg-blue-500 flex items-center justify-center transition-colors" title="Download"><Download size={13} className="text-white" /></a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Image Preview Lightbox */}
      {previewAtt && (
        <div className="fixed inset-0 z-[11000] flex items-center justify-center p-8" style={{ backgroundColor: "rgba(0,0,0,0.95)" }} onClick={() => setPreviewAtt(null)}>
          <button className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" onClick={() => setPreviewAtt(null)}><X size={16} className="text-white" /></button>
          <div className="flex flex-col items-center gap-3 max-w-4xl max-h-full" onClick={e => e.stopPropagation()}>
            <img src={`${apiBaseUrl}/TicketAttachments/file/${previewAtt.id}`} alt={previewAtt.fileName} className="max-w-full max-h-[80vh] rounded-xl border border-slate-700 object-contain" />
            <div className="flex items-center gap-3">
              <span className="text-slate-300 text-sm">{previewAtt.fileName}</span>
              <a href={`${apiBaseUrl}/TicketAttachments/file/${previewAtt.id}`} download={previewAtt.fileName} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors"><Download size={12} />Download</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── All Follow-Ups Modal (VIEW ONLY) ─────────────────────────────────────────
function AllFollowUpsModal({ ticket, onClose, formatDate, apiBaseUrl }) {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${apiBaseUrl}/TicketFollowUps/${encodeURIComponent(ticket.ticketNumber)}`)
      .then(r => r.ok ? r.json() : [])
      .then(setFollowUps)
      .catch(() => setFollowUps([]))
      .finally(() => setLoading(false));
  }, [ticket.ticketNumber]);

  return (
    <div className="fixed inset-0 z-[10999] flex items-center justify-center p-6" style={{ backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }} onClick={e => e.stopPropagation()}>
      <div className="w-full max-w-md flex flex-col rounded-2xl overflow-hidden" style={{ background: "linear-gradient(160deg,#0f172a,#1e293b)", border: "1px solid rgba(148,163,184,0.12)", boxShadow: "0 30px 80px rgba(0,0,0,0.9)", maxHeight: "calc(100vh - 80px)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ background: "linear-gradient(135deg,#1e3a5f,#1d4ed8)", borderBottom: "1px solid rgba(59,130,246,0.25)" }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Bell size={15} className="text-white" /></div>
            <div>
              <p className="text-white font-bold text-sm">Follow-Up Notes</p>
              <p className="text-blue-200/60 text-xs">{ticket.ticketNumber} · {followUps.length} note{followUps.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><X size={14} className="text-white" /></button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
          {loading && <div className="flex justify-center py-10"><div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>}

          {!loading && followUps.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-3">
                <Bell size={20} className="text-blue-500/40" />
              </div>
              <p className="text-slate-500 text-sm">No follow-ups yet</p>
              <p className="text-slate-600 text-xs mt-1">Support will add notes here</p>
            </div>
          )}

          {!loading && followUps.map((f, idx) => (
            <div key={f.followUpID} className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4 transition-all hover:border-slate-600/60">
              {/* Index badge */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-blue-400 text-xs font-bold">{followUps.length - idx}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-200 text-sm leading-relaxed">{f.message}</p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Clock size={10} className="text-slate-600" />
                    <span className="text-slate-500 text-xs">{formatDate ? formatDate(f.createdAt) : f.createdAt}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Attachments Preview (right panel widget) ─────────────────────────────────
function AttachmentsWidget({ ticketNumber, apiBaseUrl, onViewAll }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${apiBaseUrl}/TicketAttachments/${encodeURIComponent(ticketNumber)}`)
      .then(r => r.ok ? r.json() : [])
      .then(setAttachments)
      .catch(() => setAttachments([]))
      .finally(() => setLoading(false));
  }, [ticketNumber]);

  const isImage = (ct, fn) => {
    const ext = (fn || "").split(".").pop()?.toLowerCase();
    return (ct || "").includes("image") || ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  };

  const getExt = (fn) => (fn || "").split(".").pop()?.toUpperCase().slice(0, 4) || "FILE";

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Paperclip size={13} className="text-slate-400" />
          <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Attachments</p>
          {attachments.length > 0 && <span className="bg-slate-700 text-slate-300 text-xs px-1.5 py-0.5 rounded-full font-semibold">{attachments.length}</span>}
        </div>
      </div>

      {loading && <div className="flex justify-center py-3"><div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>}

      {!loading && attachments.length === 0 && (
        <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-3 text-center">
          <Paperclip size={14} className="text-slate-600 mx-auto mb-1" />
          <p className="text-slate-600 text-xs">No attachments</p>
        </div>
      )}

      {!loading && attachments.length > 0 && (
        <>
          {/* Image thumbnails */}
          {attachments.filter(a => isImage(a.contentType, a.fileName)).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {attachments.filter(a => isImage(a.contentType, a.fileName)).slice(0, 4).map(att => (
                <div key={att.id} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-slate-700/50 hover:border-blue-500/50 transition-all cursor-pointer" onClick={onViewAll}>
                  <img src={`${apiBaseUrl}/TicketAttachments/file/${att.id}`} alt={att.fileName} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Eye size={12} className="text-white" />
                  </div>
                </div>
              ))}
              {attachments.filter(a => isImage(a.contentType, a.fileName)).length > 4 && (
                <div className="w-14 h-14 rounded-lg bg-slate-700/50 border border-slate-600/40 flex items-center justify-center cursor-pointer hover:bg-slate-700 transition-colors" onClick={onViewAll}>
                  <span className="text-slate-300 text-xs font-bold">+{attachments.filter(a => isImage(a.contentType, a.fileName)).length - 4}</span>
                </div>
              )}
            </div>
          )}

          {/* Non-image preview (max 2) */}
          {attachments.filter(a => !isImage(a.contentType, a.fileName)).slice(0, 2).map(att => (
            <div key={att.id} className="flex items-center gap-2 bg-slate-800/40 border border-slate-700/30 rounded-lg px-2.5 py-2 mb-1.5 hover:border-slate-600/50 transition-all cursor-pointer group" onClick={onViewAll}>
              <div className="w-7 h-7 rounded-md bg-slate-700/60 flex items-center justify-center shrink-0">
                <span className="text-slate-300 text-xs font-bold" style={{ fontSize: 9 }}>{getExt(att.fileName)}</span>
              </div>
              <p className="text-slate-400 text-xs truncate flex-1">{att.fileName}</p>
              <Eye size={11} className="text-slate-600 group-hover:text-slate-400 transition-colors shrink-0" />
            </div>
          ))}

          {/* View All button */}
          <button
            onClick={onViewAll}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg mt-1 transition-all text-xs font-medium"
            style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.18)", color: "#93c5fd" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(59,130,246,0.18)"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.4)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(59,130,246,0.08)"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.18)"; }}
          >
            <Eye size={12} />
            View All Attachments
          </button>
        </>
      )}
    </div>
  );
}

// ─── Follow-Ups Widget (right panel, VIEW ONLY) ───────────────────────────────
function FollowUpsWidget({ ticket, formatDate, apiBaseUrl, onViewAll }) {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${apiBaseUrl}/TicketFollowUps/${encodeURIComponent(ticket.ticketNumber)}`)
      .then(r => r.ok ? r.json() : [])
      .then(setFollowUps)
      .catch(() => setFollowUps([]))
      .finally(() => setLoading(false));
  }, [ticket.ticketNumber]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell size={13} className="text-slate-400" />
          <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Follow-Ups</p>
          {followUps.length > 0 && <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">{followUps.length}</span>}
        </div>
      </div>

      {loading && <div className="flex justify-center py-3"><div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>}

      {!loading && followUps.length === 0 && (
        <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-3 text-center">
          <Bell size={14} className="text-slate-600 mx-auto mb-1" />
          <p className="text-slate-600 text-xs">No follow-ups yet</p>
        </div>
      )}

      {!loading && followUps.length > 0 && (
        <>
          {/* Latest note preview */}
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 mb-2">
            <p className="text-slate-300 text-xs leading-relaxed line-clamp-3">{followUps[0].message}</p>
            <div className="flex items-center gap-1.5 mt-2">
              <Clock size={9} className="text-slate-600" />
              <span className="text-slate-600 text-xs">{formatDate ? formatDate(followUps[0].createdAt) : followUps[0].createdAt}</span>
            </div>
          </div>

          {/* View All button */}
          <button
            onClick={onViewAll}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all text-xs font-medium"
            style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.18)", color: "#93c5fd" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(59,130,246,0.18)"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.4)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(59,130,246,0.08)"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.18)"; }}
          >
            <Eye size={12} />
            View All ({followUps.length})
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main TicketModal ─────────────────────────────────────────────────────────
export default function TicketModal({ ticket, onClose, formatDate, getPriorityColor, apiBaseUrl = config.API_BASE_URL, onStatusChange }) {
  const [activities, setActivities] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [profilePictureCache, setProfilePictureCache] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(ticket.status);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [showFollowUpsModal, setShowFollowUpsModal] = useState(false);
  const imageInputRef = useRef(null);
  const API_BASE = apiBaseUrl;

  const getCurrentUser = () => {
    try {
      const adminData = localStorage.getItem("admin");
      const userData = localStorage.getItem("user");
      if (adminData) { const a = JSON.parse(adminData); return { userId: a.username || a.email || "admin", userName: a.fullNames || a.username || "Admin User" }; }
      if (userData) { const u = JSON.parse(userData); return { userId: u.username || u.email || "user", userName: u.fullNames || u.username || "User" }; }
      return { userId: "anonymous", userName: "Anonymous User" };
    } catch { return { userId: "anonymous", userName: "Anonymous User" }; }
  };

  const [userProfilePic, setUserProfilePic] = useState(null);
  useEffect(() => {
    if (!ticket.email) return;
    fetch(`${API_BASE}/Users/get-profile-picture/${encodeURIComponent(ticket.email)}`)
      .then(r => r.ok ? r.blob() : null).then(b => b && setUserProfilePic(URL.createObjectURL(b))).catch(() => { });
  }, [ticket.email]);

  const currentUser = getCurrentUser();

  const fetchUserProfilePicture = async (userId) => {
    if (profilePictureCache[userId]) return profilePictureCache[userId];
    try {
      const r = await fetch(`${API_BASE}/Admin/get-profile-picture/${userId}`);
      if (r.ok) { const url = URL.createObjectURL(await r.blob()); setProfilePictureCache(p => ({ ...p, [userId]: url })); return url; }
      return null;
    } catch { return null; }
  };

  const getUserAvatar = (userName, profilePicture) => {
    if (profilePicture) return <img src={profilePicture} alt={userName} className="w-full h-full object-cover" />;
    return <span className="text-white font-bold text-sm">{userName.charAt(0).toUpperCase()}</span>;
  };

  const fileToBase64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.readAsDataURL(file); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; });

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setSelectedImage(file); const r = new FileReader(); r.onloadend = () => setImagePreview(r.result); r.readAsDataURL(file); }
  };

  const clearAttachments = () => { setSelectedImage(null); setImagePreview(null); if (imageInputRef.current) imageInputRef.current.value = ''; };

  useEffect(() => { fetchActivities(); }, [ticket.ticketNumber]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/Activities/ticket/${encodeURIComponent(ticket.ticketNumber)}?userId=${encodeURIComponent(currentUser.userId)}`);
      if (res.ok) {
        const data = await res.json();
        const withPics = await Promise.all(data.map(async act => {
          const pic = await fetchUserProfilePicture(act.userId);
          const comments = await Promise.all((act.comments || []).map(async c => {
            const cp = await fetchUserProfilePicture(c.userId);
            const replies = await Promise.all((c.replies || []).map(async r => ({ ...r, profilePicture: await fetchUserProfilePicture(r.userId) })));
            return { ...c, profilePicture: cp, replies };
          }));
          return { ...act, profilePicture: pic, comments };
        }));
        setActivities(withPics);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handlePostActivity = async () => {
    if (!newPost.trim() && !selectedImage) { alert("Please add content or image."); return; }
    try {
      const payload = { ticketNumber: ticket.ticketNumber, userId: currentUser.userId, userName: currentUser.userName, content: newPost.trim() || " " };
      if (selectedImage) payload.imageBase64 = await fileToBase64(selectedImage);
      const res = await fetch(`${API_BASE}/Activities`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (res.ok) { setNewPost(""); clearAttachments(); fetchActivities(); }
      else alert(`Failed to post: ${await res.text()}`);
    } catch { alert("Error posting activity."); }
  };

  const handlePostComment = async (activityId, parentCommentId = null) => {
    if (!newPost.trim()) return;
    try {
      const payload = { activityId, userId: currentUser.userId, userName: currentUser.userName, content: newPost };
      if (parentCommentId) payload.parentCommentId = parentCommentId;
      const res = await fetch(`${API_BASE}/Activities/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (res.ok) { setNewPost(""); setReplyingTo(null); fetchActivities(); }
    } catch (e) { console.error(e); }
  };

  const handleDeleteActivity = async (id) => { try { const r = await fetch(`${API_BASE}/Activities/${id}`, { method: "DELETE" }); if (r.ok) fetchActivities(); } catch (e) { console.error(e); } };
  const handleDeleteComment = async (id) => { try { const r = await fetch(`${API_BASE}/Activities/comments/${id}`, { method: "DELETE" }); if (r.ok) fetchActivities(); } catch (e) { console.error(e); } };

  const handleActivityReaction = async (activityId, reactionType) => {
    try {
      const cur = activities.find(a => a.activityId === activityId)?.reactionSummary?.userReaction;
      if (cur === reactionType) { const r = await fetch(`${API_BASE}/Activities/${activityId}/reactions/${currentUser.userId}`, { method: "DELETE" }); if (r.ok) fetchActivities(); }
      else { const r = await fetch(`${API_BASE}/Activities/${activityId}/reactions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: currentUser.userId, userName: currentUser.userName, reactionType }) }); if (r.ok) fetchActivities(); }
    } catch (e) { console.error(e); }
  };

  const handleCommentReaction = async (commentId, reactionType) => {
    try {
      let cur = null;
      outer: for (const act of activities) {
        for (const c of act.comments || []) {
          if (c.commentId === commentId) { cur = c.reactionSummary?.userReaction ?? null; break outer; }
          for (const r of c.replies || []) { if (r.commentId === commentId) { cur = r.reactionSummary?.userReaction ?? null; break outer; } }
        }
      }
      const payload = { userId: currentUser.userId, userName: currentUser.userName, reactionType };
      if (cur === reactionType) { const r = await fetch(`${API_BASE}/CommentReactionsV2/${commentId}/reactions/${encodeURIComponent(currentUser.userId)}`, { method: "DELETE" }); if (r.ok) fetchActivities(); }
      else { const r = await fetch(`${API_BASE}/CommentReactionsV2/${commentId}/reactions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }); if (r.ok) fetchActivities(); }
    } catch (e) { console.error(e); }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (currentStatus === newStatus) return;
    try {
      setUpdatingStatus(true);
      const res = await fetch(`${API_BASE}/Tickets/${ticket.ticketNumber}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...ticket, status: newStatus }) });
      if (res.ok) {
        setCurrentStatus(newStatus);
        if (onStatusChange) onStatusChange(ticket.ticketNumber, newStatus);
        // ✅ POST RESOLUTION ACTIVITY when marked Resolved
        if (newStatus === "Resolved") {
          try {
            const storageKey = `timer_pause_${ticket.ticketNumber}`;
            const pauseState = JSON.parse(localStorage.getItem(storageKey) || "{}");
            const pausedAccum = pauseState.pausedAccum || 0;
            const startedAt = ticket.startedAt || ticket.StartedAt;
            const fmtDur = (s) => {
              if (!s || s <= 0) return "00:00:00";
              return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
            };
            const timeSpentSecs = startedAt
              ? Math.max(Math.floor((new Date() - new Date(startedAt)) / 1000) - pausedAccum, 0)
              : null;
            const deadline = ticket.deadline || ticket.Deadline;
            const remainingSecs = deadline ? Math.floor((new Date(deadline) - new Date()) / 1000) : null;

            // Save to DB
            await fetch(`${API_BASE}/Tickets/${ticket.ticketNumber}/timeTracking`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                timeSpent: timeSpentSecs ?? 0,
                timeRemaining: remainingSecs !== null ? Math.max(remainingSecs, 0) : 0,
              }),
            }).catch(e => console.warn("timeTracking save failed:", e));

            const timeSpentLabel = timeSpentSecs !== null ? fmtDur(timeSpentSecs) : "Not tracked";
            const remainingLabel = remainingSecs === null
              ? "No deadline"
              : remainingSecs <= 0
                ? `⚠ Overdue by ${fmtDur(Math.abs(remainingSecs))}`
                : fmtDur(remainingSecs);

            // Post to Activity Feed
            await fetch(`${API_BASE}/Activities`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ticketNumber: ticket.ticketNumber,
                userId: currentUser.userId,
                userName: currentUser.userName,
                content: `✅ Ticket Resolved\n⏱ Time Spent: ${timeSpentLabel}\n⏳ Time Remaining at close: ${remainingLabel}`,
              }),
            });

            // Clear timer
            localStorage.removeItem(storageKey);
            fetchActivities();
          } catch (e) {
            console.warn("Resolution activity post failed:", e);
          }
        }
        if (ticket.email) {
          const msgs = { "In Progress": { headline: "Your ticket is now being worked on!", detail: "A support agent has picked up your ticket." }, "Resolved": { headline: "Your ticket has been resolved!", detail: "Our support team has resolved your issue." }, "Closed": { headline: "Your ticket has been closed.", detail: "Thank you for reaching out!" }, "Pending": { headline: "Your ticket is pending review.", detail: "Your ticket is queued and waiting to be assigned." } };
          const info = msgs[newStatus];
          if (info) fetch(`${API_BASE}/Email/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ toEmail: ticket.email, toName: ticket.name || "User", subject: `[Ticket Update] ${ticket.ticketNumber} - ${newStatus}`, body: `Hi ${ticket.name || "User"},\n\n${info.headline}\n\n${info.detail}\n\nTicket: ${ticket.ticketNumber}\nStatus: ${newStatus}\n\nOrca Helpdesk Team` }) }).catch(() => { });
        }
      } else alert(`Failed: ${await res.text()}`);
    } catch { alert("Error updating status."); } finally { setUpdatingStatus(false); }
  };

  const getNextAction = (s) => {
    if (s === "Pending") return { label: "Move to In Progress", next: "In Progress", icon: Clock, color: "bg-amber-500 hover:bg-amber-600", iconColor: "text-amber-200" };
    if (s === "In Progress") return { label: "Mark as Resolved", next: "Resolved", icon: CheckCircle, color: "bg-green-500 hover:bg-green-600", iconColor: "text-green-200" };
    return null;
  };
  const getStatusColor = (s) => ({ Pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", "In Progress": "bg-blue-500/15 text-blue-400 border-blue-500/30", Resolved: "bg-green-500/15 text-green-400 border-green-500/30" }[s] || "bg-slate-500/15 text-slate-400 border-slate-500/30");
  const getPriorityBadge = (p) => ({ Low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Medium: "bg-amber-500/15 text-amber-400 border-amber-500/30", High: "bg-red-500/15 text-red-400 border-red-500/30", Critical: "bg-red-600/20 text-red-300 border-red-600/40" }[p] || "bg-slate-500/15 text-slate-400 border-slate-500/30");

  const nextAction = getNextAction(currentStatus);

  return (
    <>
      <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col rounded-2xl shadow-2xl" style={{ background: "linear-gradient(135deg,#0f172a,#1e293b)", border: "1px solid rgba(148,163,184,0.12)" }}>

          {/* HEADER */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50" style={{ background: "linear-gradient(90deg,hsl(224,76%,48%),hsl(224,76%,48%))" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center"><MessageCircle size={18} className="text-white" /></div>
              <div>
                <h2 className="text-white font-bold text-lg leading-tight">{ticket.ticketNumber}</h2>
                <p className="text-blue-200 text-xs">{formatDate ? formatDate(ticket.createdAt) : ticket.createdAt}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full border text-xs font-medium ${getStatusColor(currentStatus)}`}>{currentStatus}</span>
              <span className={`px-3 py-1 rounded-full border text-xs font-medium ${getPriorityBadge(ticket.priority)}`}>{ticket.priority || "N/A"}</span>
              <button onClick={onClose} className="ml-2 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><X size={16} className="text-white" /></button>
            </div>
          </div>

          {/* 3-COLUMN BODY */}
          <div className="flex-1 overflow-hidden flex">

            {/* LEFT */}
            <div className="w-72 flex-shrink-0 border-r border-slate-700/50 overflow-y-auto p-5 space-y-5">
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Ticket Details</p>
                <div className="mb-3"><p className="text-slate-500 text-xs mb-1">Subject</p><p className="text-white text-sm font-semibold">{ticket.subject || ticket.title || "—"}</p></div>
                <div className="space-y-2.5">
                  <DetailRow label="Ticket ID" value={ticket.ticketNumber} />
                  <DetailRow label="Category" value={ticket.category} />
                  <DetailRow label="Priority" value={ticket.priority} highlight={getPriorityBadge(ticket.priority)} />
                  <DetailRow label="Status" value={currentStatus} highlight={getStatusColor(currentStatus)} />
                  <DetailRow label="Submitted" value={formatDate ? formatDate(ticket.createdAt) : ticket.createdAt} />
                </div>
              </div>

              <div>
                <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Update Status</p>
                <div className="flex items-center gap-1 mb-3">
                  {[{ label: "Pending", color: "bg-yellow-500" }, { label: "In Progress", color: "bg-blue-500" }, { label: "Resolved", color: "bg-green-500" }].map((s, i, arr) => (
                    <div key={s.label} className="flex items-center gap-1 flex-1">
                      <div className={`flex-1 h-1.5 rounded-full transition-all ${currentStatus === s.label ? s.color : (i === 0 && (currentStatus === "In Progress" || currentStatus === "Resolved")) || (i === 1 && currentStatus === "Resolved") ? s.color + " opacity-70" : "bg-slate-700"}`} />
                      {i < arr.length - 1 && <ArrowRight size={8} className="text-slate-600 flex-shrink-0" />}
                    </div>
                  ))}
                </div>
                {nextAction ? (
                  <button onClick={() => handleStatusUpdate(nextAction.next)} disabled={updatingStatus} className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-white text-xs font-semibold transition-all shadow-lg disabled:opacity-60 ${nextAction.color}`}>
                    {updatingStatus ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <nextAction.icon size={14} className={nextAction.iconColor} />}
                    {updatingStatus ? "Updating..." : nextAction.label}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-green-500/10 border border-green-500/30 rounded-xl">
                    <CheckCircle size={14} className="text-green-400" /><span className="text-green-400 text-xs font-semibold">Ticket Resolved</span>
                  </div>
                )}
                <div className="flex gap-1.5 mt-2">
                  {[{ status: "Pending", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/20" }, { status: "In Progress", color: "bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20" }, { status: "Resolved", color: "bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20" }].map(({ status, color }) => (
                    <button key={status} onClick={() => handleStatusUpdate(status)} disabled={updatingStatus || currentStatus === status} className={`flex-1 px-1.5 py-1.5 rounded-lg border text-xs font-medium transition-all disabled:opacity-40 ${color} ${currentStatus === status ? "ring-1 ring-offset-1 ring-offset-slate-900" : ""}`}>
                      {status === "Pending" ? "Pending" : status === "In Progress" ? "In Prog." : "Done"}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Submitted By</p>
                <div className="bg-slate-800/40 rounded-xl p-3 border border-slate-700/40">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {userProfilePic ? <img src={userProfilePic} alt="user" className="w-full h-full object-cover" /> : <span className="text-white font-bold text-sm">{(ticket.name || "U").charAt(0).toUpperCase()}</span>}
                    </div>
                    <div><p className="text-white text-sm font-medium">{ticket.name || "—"}</p><p className="text-slate-400 text-xs">{ticket.email || "—"}</p></div>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Quick Stats</p>
                    <div className="space-y-2">
                      <StatRow label="Days Open" value={ticket.daysOpen || "—"} valueColor="text-blue-400" />
                      <StatRow label="Priority" value={ticket.priority || "—"} valueColor={ticket.priority === "Low" ? "text-emerald-400" : ticket.priority === "Medium" ? "text-amber-400" : "text-red-400"} />
                      <StatRow label="Stage" value={currentStatus || "—"} valueColor={currentStatus === "Pending" ? "text-yellow-400" : currentStatus === "In Progress" ? "text-blue-400" : "text-green-400"} />
                      <TimeTracker ticket={ticket} onActivityPosted={fetchActivities} />                </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-700/50 bg-slate-900/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Progress</span>
                  <span className="text-blue-400 text-xs font-bold">{currentStatus === "Resolved" ? "100%" : currentStatus === "In Progress" ? "50%" : "10%"}</span>
                </div>
                <div className="w-full bg-slate-700/50 rounded-full h-1.5 mb-3">
                  <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: currentStatus === "Resolved" ? "100%" : currentStatus === "In Progress" ? "50%" : "10%", background: "linear-gradient(90deg,#3b82f6,#06b6d4)" }} />
                </div>
                <div className="flex items-center justify-between">
                  {[{ label: "Submitted", done: true }, { label: "In Review", done: currentStatus === "In Progress" || currentStatus === "Resolved" }, { label: "Resolved", done: currentStatus === "Resolved" }].map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${s.done ? "bg-blue-500" : "bg-slate-700"}`}>{s.done && <CheckCircle size={10} className="text-white" />}</div>
                      <span className={`text-xs ${s.done ? "text-blue-400 font-medium" : "text-slate-500"}`}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold mb-4">Activity Feed</p>
                {loading ? (
                  <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>
                ) : activities.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 text-sm">No activities yet. Be the first to post!</div>
                ) : activities.map(activity => (
                  <ActivityPost key={activity.activityId} activity={activity} onDelete={handleDeleteActivity} onReply={(aId, cId, uName) => setReplyingTo({ activityId: aId, commentId: cId, userName: uName })} onPostComment={handlePostComment} onDeleteComment={handleDeleteComment} onActivityReaction={handleActivityReaction} onCommentReaction={handleCommentReaction} replyingTo={replyingTo} setReplyingTo={setReplyingTo} newPost={newPost} setNewPost={setNewPost} formatDate={formatDate} getUserAvatar={getUserAvatar} currentUser={currentUser} API_BASE={API_BASE} />
                ))}
              </div>

              {!replyingTo && (
                <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-900/20">
                  {imagePreview && (
                    <div className="mb-2 relative inline-block">
                      <img src={imagePreview} alt="Preview" className="max-h-28 rounded-lg border border-slate-700" />
                      <button onClick={clearAttachments} className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5"><X size={12} className="text-white" /></button>
                    </div>
                  )}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-xl border border-slate-700/50 px-4 py-2.5">
                    <input value={newPost} onChange={e => setNewPost(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handlePostActivity(); } }} placeholder="Add a comment..." className="flex-1 bg-transparent text-white placeholder-slate-500 text-sm outline-none" />
                    <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                    <button onClick={() => imageInputRef.current?.click()} className="text-slate-400 hover:text-blue-400 transition-colors p-1"><Image size={16} /></button>
                    <button onClick={handlePostActivity} disabled={!newPost.trim() && !selectedImage} className="w-8 h-8 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:opacity-50 flex items-center justify-center transition-colors"><Send size={14} className="text-white" /></button>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT */}
            <div className="w-64 flex-shrink-0 border-l border-slate-700/50 overflow-y-auto p-5 space-y-5">

              <SLASection ticket={ticket} currentStatus={currentStatus} apiBaseUrl={API_BASE} />

              <div className="border-t border-slate-700/50" />

              {/* Due Date */}
              {ticket.deadline && (() => {
                const now = new Date(), due = new Date(ticket.deadline), diffMs = due - now, diffDays = diffMs / 86400000;
                const isDone = ticket.status === "Resolved" || ticket.status === "Closed";
                const state = isDone ? "done" : diffMs < 0 ? "overdue" : diffDays <= 1 ? "critical" : diffDays <= 3 ? "warning" : "safe";
                const c = { done: { bg: "bg-blue-500/10", border: "border-blue-500/30", icon: "text-blue-400", label: "text-blue-300", sub: "text-blue-500", dot: "bg-blue-400", sub2: "Completed" }, overdue: { bg: "bg-red-500/10", border: "border-red-500/30", icon: "text-red-400", label: "text-red-300", sub: "text-red-500", dot: "bg-red-400", sub2: "Overdue" }, critical: { bg: "bg-red-500/10", border: "border-red-500/30", icon: "text-red-400", label: "text-red-300", sub: "text-red-400", dot: "bg-red-400", sub2: "Due today" }, warning: { bg: "bg-amber-500/10", border: "border-amber-500/30", icon: "text-amber-400", label: "text-amber-300", sub: "text-amber-500", dot: "bg-amber-400", sub2: `${Math.ceil(diffDays)}d left` }, safe: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: "text-emerald-400", label: "text-emerald-300", sub: "text-emerald-600", dot: "bg-emerald-400", sub2: `${Math.ceil(diffDays)}d remaining` } }[state];
                return (
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Due Date</p>
                    <div className={`rounded-xl p-3 border ${c.bg} ${c.border}`}>
                      <div className="flex items-center gap-2 mb-1.5"><div className={`w-2 h-2 rounded-full ${c.dot} ${state === "critical" || state === "overdue" ? "animate-pulse" : ""}`} /><span className={`text-xs font-bold uppercase tracking-wide ${c.sub}`}>{c.sub2}</span></div>
                      <div className="flex items-center gap-2"><Clock size={13} className={c.icon} /><span className={`text-sm font-semibold ${c.label}`}>{due.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></div>
                      <p className="text-slate-500 text-xs mt-1 ml-5">{due.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                );
              })()}

              <div className="border-t border-slate-700/50" />

              {/* ── ATTACHMENTS WIDGET ── */}
              <AttachmentsWidget ticketNumber={ticket.ticketNumber} apiBaseUrl={API_BASE} onViewAll={() => setShowAttachmentsModal(true)} />

              <div className="border-t border-slate-700/50" />

              {/* ── FOLLOW-UPS WIDGET ── */}
              <FollowUpsWidget ticket={ticket} formatDate={formatDate} apiBaseUrl={API_BASE} onViewAll={() => setShowFollowUpsModal(true)} />

              <div className="border-t border-slate-700/50" />

              {/* Assigned To */}
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Assigned To</p>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-300 font-bold text-sm">{(ticket.assignees || "U").charAt(0).toUpperCase()}</span>
                  </div>
                  <div><p className="text-white text-sm font-medium">{ticket.assignees || "Unassigned"}</p><p className="text-slate-500 text-xs">Assignee</p></div>
                </div>
              </div>

              {/* Quick Stats */}
              {/* Quick Stats */}


              {/* Labels */}
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Labels</p>
                <div className="flex flex-wrap gap-1.5">
                  {ticket.category && <span className="px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-full text-xs">{ticket.category}</span>}
                  {ticket.priority && <span className={`px-2 py-1 rounded-full border text-xs ${getPriorityBadge(ticket.priority)}`}>{ticket.priority}</span>}
                  {!ticket.category && !ticket.priority && <span className="text-slate-500 text-xs">No labels</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAttachmentsModal && <AllAttachmentsModal ticketNumber={ticket.ticketNumber} onClose={() => setShowAttachmentsModal(false)} formatDate={formatDate} apiBaseUrl={API_BASE} />}
      {showFollowUpsModal && <AllFollowUpsModal ticket={ticket} onClose={() => setShowFollowUpsModal(false)} formatDate={formatDate} apiBaseUrl={API_BASE} />}
    </>
  );
}

function StatRow({ label, value, valueColor = "text-slate-200" }) {
  return <div className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2 border border-slate-700/40"><span className="text-slate-400 text-xs">{label}</span><span className={`text-xs font-semibold ${valueColor}`}>{value}</span></div>;
}

function DetailRow({ label, value, highlight }) {
  return <div className="flex flex-col gap-0.5"><span className="text-slate-500 text-xs">{label}</span>{highlight ? <span className={`px-2 py-0.5 rounded-md border text-xs font-medium w-fit ${highlight}`}>{value || "N/A"}</span> : <span className="text-slate-200 text-xs font-medium">{value || "—"}</span>}</div>;
}

function TimeTracker({ ticket, onActivityPosted }) {
  const storageKey = `timer_pause_${ticket.ticketNumber}`;

  const [now, setNow] = useState(new Date());
  const [isPaused, setIsPaused] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey))?.isPaused || false; } catch { return false; }
  });
  const [pausedAccum, setPausedAccum] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey))?.pausedAccum || 0; } catch { return 0; }
  });
  const [pausedAt, setPausedAt] = useState(() => {
    try { const d = JSON.parse(localStorage.getItem(storageKey))?.pausedAt; return d ? new Date(d) : null; } catch { return null; }
  });

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [isPaused]);

  const formatDuration = (seconds) => {
    if (seconds <= 0) return "00:00:00";
    const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const handlePauseResume = async () => {
    if (isPaused) {
      // RESUME — ask for reason
      const { value: resumeNote, isConfirmed } = await Swal.fire({
        title: "▶ Resume Timer?",
        html: `<div style="font-size:13px;color:#94a3b8;margin-bottom:8px">Add a note for resuming <strong style="color:#60a5fa">${ticket.ticketNumber}</strong></div>
             <textarea id="swal-resume-note" class="swal2-textarea" placeholder="e.g. Back from break, continuing investigation..." style="font-size:13px;background:#1e293b;color:#f1f5f9;border:1px solid #334155;resize:none;height:80px;width:100%;box-sizing:border-box;padding:8px;border-radius:8px"></textarea>`,
        showCancelButton: true,
        confirmButtonText: "▶ Resume",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#22c55e",
        cancelButtonColor: "#64748b",
        background: "#1e293b",
        color: "#f1f5f9",
        preConfirm: () => document.getElementById("swal-resume-note").value.trim() || "Resumed working on ticket.",
      });
      if (!isConfirmed) return;

      const addedPause = pausedAt ? Math.floor((new Date() - pausedAt) / 1000) : 0;
      const newAccum = pausedAccum + addedPause;
      const pausedDur = formatDuration(addedPause);
      setPausedAccum(newAccum);
      setPausedAt(null);
      setIsPaused(false);
      setNow(new Date());
      localStorage.setItem(storageKey, JSON.stringify({ isPaused: false, pausedAccum: newAccum, pausedAt: null }));

      // Post to Activity Feed
      await postTimerActivity(`▶ Resumed | Paused for ${pausedDur}\n📝 ${resumeNote}`);

    } else {
      // PAUSE — ask for reason
      const { value: pauseNote, isConfirmed } = await Swal.fire({
        title: "⏸ Pause Timer?",
        html: `<div style="font-size:13px;color:#94a3b8;margin-bottom:8px">Why are you pausing <strong style="color:#60a5fa">${ticket.ticketNumber}</strong>?</div>
             <textarea id="swal-pause-note" class="swal2-textarea" placeholder="e.g. Waiting for user response, on lunch break..." style="font-size:13px;background:#1e293b;color:#f1f5f9;border:1px solid #334155;resize:none;height:80px;width:100%;box-sizing:border-box;padding:8px;border-radius:8px"></textarea>`,
        showCancelButton: true,
        confirmButtonText: "⏸ Pause",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#eab308",
        cancelButtonColor: "#64748b",
        background: "#1e293b",
        color: "#f1f5f9",
        preConfirm: () => document.getElementById("swal-pause-note").value.trim() || "Timer paused.",
      });
      if (!isConfirmed) return;

      const pAt = new Date();
      setPausedAt(pAt);
      setIsPaused(true);
      localStorage.setItem(storageKey, JSON.stringify({ isPaused: true, pausedAccum, pausedAt: pAt.toISOString() }));

      // Post to Activity Feed
      await postTimerActivity(`⏸ Paused at ${pAt.toLocaleTimeString()}\n📝 ${pauseNote}`);
    }
  };

  const postTimerActivity = async (message) => {
    try {
      const adminData = JSON.parse(localStorage.getItem("admin") || "{}");
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = adminData?.username || adminData?.email || userData?.username || userData?.email || "system";
      const userName = adminData?.fullNames || adminData?.username || userData?.fullNames || userData?.username || "System";

      await fetch(`${config.API_BASE_URL}/Activities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticketNumber: ticket.ticketNumber,
          userId,
          userName,
          content: message,
        }),
      });
    } catch (e) {

      console.warn("Timer activity post failed:", e);
    }
    if (onActivityPosted) onActivityPosted();
  };

const startedAt = ticket.startedAt || ticket.StartedAt;
const isCompleted = ticket.status === "Resolved" || ticket.status === "Closed";
const rawSpent = (!isCompleted && startedAt) ? Math.floor((now - new Date(startedAt)) / 1000) - pausedAccum : null;
const timeSpent = rawSpent !== null ? formatDuration(Math.max(rawSpent, 0)) : null;

  const deadline = ticket.deadline || ticket.Deadline;
  const remaining = deadline ? Math.floor((new Date(deadline) - now) / 1000) + (isPaused && pausedAt ? Math.floor((new Date() - pausedAt) / 1000) : 0) : null;

  return (
    <>
      {/* Time Spent */}
      <div className="bg-slate-800/40 rounded-lg px-3 py-2 border border-slate-700/40">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-slate-400 text-xs">Time Spent</span>
          {timeSpent && (
            <button
              onClick={handlePauseResume}
              title={isPaused ? "Resume timer" : "Pause timer"}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border transition-all ${isPaused
                ? "bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20"
                : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/20"
                }`}
            >
              {isPaused ? "▶ Resume" : "⏸ Pause"}
            </button>
          )}
        </div>
        {timeSpent ? (
          <div className="flex items-center gap-2">
            <span className={`font-mono text-xs font-semibold ${isPaused ? "text-yellow-400" : "text-blue-400"}`}>
              {timeSpent}
            </span>
            {isPaused && (
              <span className="text-yellow-500/70 text-xs italic animate-pulse">paused</span>
            )}
          </div>
        ) : (
          <span className="text-slate-600 text-xs italic">Not started</span>
        )}
      </div>

      {/* Time Remaining */}
      <div className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2 border border-slate-700/40">
        <span className="text-slate-400 text-xs">Remaining</span>
        {remaining !== null ? (
          remaining <= 0
            ? <span className="font-mono text-xs font-semibold text-red-400 animate-pulse">⚠ Overdue</span>
            : <span className={`font-mono text-xs font-semibold ${remaining < 3600 ? "text-red-400" : remaining < 86400 ? "text-yellow-400" : "text-green-400"}`}>
              {formatDuration(remaining)}
            </span>
        ) : (
          <span className="text-slate-600 text-xs italic">No deadline</span>
        )}
      </div>
    </>
  );
}
function ActivityPost({ activity, onDelete, onReply, onPostComment, onDeleteComment, onActivityReaction, onCommentReaction, replyingTo, setReplyingTo, newPost, setNewPost, formatDate, getUserAvatar, currentUser, API_BASE }) {
  const [showComments, setShowComments] = useState(true);
  const [showReactions, setShowReactions] = useState(false);
  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/40 p-3.5">
      <div className="flex justify-between items-start mb-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center overflow-hidden border border-blue-500/30 flex-shrink-0">{getUserAvatar(activity.userName, activity.profilePicture)}</div>
          <div><p className="text-white text-sm font-medium leading-tight">{activity.userName}</p><p className="text-slate-500 text-xs">{formatDate ? formatDate(activity.createdAt) : activity.createdAt}</p></div>
        </div>
        {activity.userId === currentUser.userId && <button onClick={() => onDelete(activity.activityId)} className="p-1 text-slate-500 hover:text-red-400 rounded transition-colors"><Trash2 size={14} /></button>}
      </div>
      {activity.content?.trim() && <p className="text-slate-200 text-sm mb-3 whitespace-pre-wrap leading-relaxed">{activity.content}</p>}
      {activity.hasImage && <div className="mb-3"><img src={`${API_BASE}/Activities/${activity.activityId}/image`} alt="Activity" className="max-h-60 rounded-lg border border-slate-700 cursor-pointer hover:opacity-90" onClick={() => window.open(`${API_BASE}/Activities/${activity.activityId}/image`, '_blank')} /></div>}
      {activity.hasAttachment && <div className="mb-3"><a href={`${API_BASE}/Activities/${activity.activityId}/attachment`} download={activity.fileName} className="flex items-center gap-2 bg-slate-900/50 rounded-lg p-2.5 border border-slate-700/50 hover:border-blue-500/50 transition-colors"><FileText size={16} className="text-blue-400 flex-shrink-0" /><div className="flex-1 min-w-0"><span className="text-xs text-slate-300 block truncate">{activity.fileName || 'Attachment'}</span>{activity.fileSize && <span className="text-xs text-slate-500">{(activity.fileSize / 1024).toFixed(2)} KB</span>}</div><Download size={14} className="text-slate-400 flex-shrink-0" /></a></div>}
      {activity.reactionSummary?.totalReactions > 0 && <div className="mb-2 flex items-center gap-1.5"><div className="flex -space-x-1">{Object.entries(activity.reactionSummary.reactionCounts || {}).map(([type, count]) => { const R = REACTION_TYPES[type]?.icon; return R ? <div key={type} className={`w-5 h-5 rounded-full bg-slate-800 border-2 border-slate-900 flex items-center justify-center ${REACTION_TYPES[type].color}`}><R size={10} /></div> : null; })}</div><span className="text-slate-400 text-xs">{activity.reactionSummary.totalReactions}</span></div>}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-700/30">
        <div className="relative">
          <button onClick={() => setShowReactions(!showReactions)} className={`text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors ${activity.reactionSummary?.userReaction ? REACTION_TYPES[activity.reactionSummary.userReaction]?.color : ''}`}>
            {activity.reactionSummary?.userReaction ? (() => { const R = REACTION_TYPES[activity.reactionSummary.userReaction]?.icon; return R ? <><R size={12} />{activity.reactionSummary.userReaction}</> : <><ThumbsUp size={12} />React</>; })() : <><ThumbsUp size={12} />React</>}
          </button>
          {showReactions && (<><div className="fixed inset-0 z-10" onClick={() => setShowReactions(false)} /><div className="absolute bottom-full left-0 mb-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1.5 flex gap-1 z-20">{Object.entries(REACTION_TYPES).map(([type, { icon: Icon, color, hoverColor }]) => (<button key={type} onClick={() => { onActivityReaction(activity.activityId, type); setShowReactions(false); }} className={`p-1.5 rounded-lg ${color} ${hoverColor} transition-colors ${activity.reactionSummary?.userReaction === type ? 'bg-slate-700' : ''}`}><Icon size={14} /></button>))}</div></>)}
        </div>
        <button onClick={() => setShowComments(!showComments)} className="text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors"><MessageCircle size={12} />{activity.commentCount} {activity.commentCount === 1 ? "comment" : "comments"}</button>
        <button onClick={() => onReply(activity.activityId, null, activity.userName)} className="text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors"><Reply size={12} />Reply</button>
      </div>
      {replyingTo?.activityId === activity.activityId && !replyingTo.commentId && (
        <div className="mt-3 ml-10 bg-slate-900/50 rounded-lg border border-slate-700/50 p-2.5">
          <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder={`Reply to ${replyingTo.userName}...`} className="w-full bg-transparent text-white placeholder-slate-500 border-0 outline-none resize-none min-h-[50px] text-xs" />
          <div className="flex justify-end gap-2 mt-1.5">
            <button onClick={() => setReplyingTo(null)} className="px-3 py-1 text-slate-400 hover:text-white text-xs rounded">Cancel</button>
            <button onClick={() => onPostComment(activity.activityId)} disabled={!newPost.trim()} className="px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-xs rounded">Reply</button>
          </div>
        </div>
      )}
      {showComments && activity.comments?.length > 0 && (
        <div className="mt-3 space-y-2 ml-10">
          {activity.comments.map(c => <Comment key={c.commentId} comment={c} activityId={activity.activityId} onDeleteComment={onDeleteComment} onReply={onReply} onPostComment={onPostComment} onCommentReaction={onCommentReaction} replyingTo={replyingTo} setReplyingTo={setReplyingTo} newPost={newPost} setNewPost={setNewPost} formatDate={formatDate} getUserAvatar={getUserAvatar} currentUser={currentUser} API_BASE={API_BASE} />)}
        </div>
      )}
    </div>
  );
}

function Comment({ comment, activityId, onDeleteComment, onReply, onPostComment, onCommentReaction, replyingTo, setReplyingTo, newPost, setNewPost, formatDate, getUserAvatar, currentUser, API_BASE }) {
  const [showReactions, setShowReactions] = useState(false);
  return (
    <div className="bg-slate-800/30 rounded-lg p-2.5">
      <div className="flex justify-between items-start mb-1.5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center overflow-hidden border border-blue-500/30 flex-shrink-0">{getUserAvatar(comment.userName, comment.profilePicture)}</div>
          <div><p className="text-white text-xs font-medium">{comment.userName}</p><p className="text-slate-500 text-xs">{formatDate ? formatDate(comment.createdAt) : comment.createdAt}</p></div>
        </div>
        {comment.userId === currentUser.userId && <button onClick={() => onDeleteComment(comment.commentId)} className="p-0.5 text-slate-500 hover:text-red-400 rounded"><Trash2 size={12} /></button>}
      </div>
      <p className="text-slate-300 text-xs mb-2 ml-9">{comment.content}</p>
      {comment.reactionSummary?.totalReactions > 0 && <div className="mb-1.5 ml-9 flex items-center gap-1"><div className="flex -space-x-1">{Object.entries(comment.reactionSummary.reactionCounts || {}).map(([type, count]) => { const R = REACTION_TYPES[type]?.icon; return R ? <div key={type} className={`w-4 h-4 rounded-full bg-slate-800 border border-slate-900 flex items-center justify-center ${REACTION_TYPES[type].color}`}><R size={8} /></div> : null; })}</div><span className="text-slate-400 text-xs">{comment.reactionSummary.totalReactions}</span></div>}
      <div className="flex items-center gap-3 ml-9">
        <div className="relative">
          <button onClick={() => setShowReactions(!showReactions)} className={`text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 ${comment.reactionSummary?.userReaction ? REACTION_TYPES[comment.reactionSummary.userReaction]?.color : ''}`}>
            {comment.reactionSummary?.userReaction ? (() => { const R = REACTION_TYPES[comment.reactionSummary.userReaction]?.icon; return R ? <><R size={10} />{comment.reactionSummary.userReaction}</> : <><ThumbsUp size={10} />React</>; })() : <><ThumbsUp size={10} />React</>}
          </button>
          {showReactions && (<><div className="fixed inset-0 z-10" onClick={() => setShowReactions(false)} /><div className="absolute bottom-full left-0 mb-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1 flex gap-0.5 z-20">{Object.entries(REACTION_TYPES).map(([type, { icon: Icon, color, hoverColor }]) => (<button key={type} onClick={() => { onCommentReaction(comment.commentId, type); setShowReactions(false); }} className={`p-1 rounded-lg ${color} ${hoverColor} ${comment.reactionSummary?.userReaction === type ? 'bg-slate-700' : ''}`}><Icon size={12} /></button>))}</div></>)}
        </div>
        <button onClick={() => onReply(activityId, comment.commentId, comment.userName)} className="text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1"><Reply size={10} />Reply</button>
      </div>
      {replyingTo?.commentId === comment.commentId && (
        <div className="mt-2 ml-9 bg-slate-700/30 rounded-lg border border-slate-600/50 p-2">
          <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder={`Reply to ${replyingTo.userName}...`} className="w-full bg-transparent text-white placeholder-slate-500 border-0 outline-none resize-none min-h-[40px] text-xs" />
          <div className="flex justify-end gap-2 mt-1">
            <button onClick={() => setReplyingTo(null)} className="px-2 py-0.5 text-slate-400 text-xs rounded">Cancel</button>
            <button onClick={() => onPostComment(activityId, comment.commentId)} disabled={!newPost.trim()} className="px-2 py-0.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-xs rounded">Reply</button>
          </div>
        </div>
      )}
      {comment.replies?.length > 0 && <div className="mt-2 ml-9 space-y-1.5">{comment.replies.map(r => <ReplyItem key={r.commentId} reply={r} onDeleteComment={onDeleteComment} onCommentReaction={onCommentReaction} formatDate={formatDate} getUserAvatar={getUserAvatar} currentUser={currentUser} API_BASE={API_BASE} />)}</div>}
    </div>
  );
}

function ReplyItem({ reply, onDeleteComment, onCommentReaction, formatDate, getUserAvatar, currentUser }) {
  const [showReactions, setShowReactions] = useState(false);
  return (
    <div className="bg-slate-700/20 rounded-lg p-2">
      <div className="flex justify-between items-start mb-1">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-blue-400 flex items-center justify-center overflow-hidden border border-green-500/30 flex-shrink-0">{getUserAvatar(reply.userName, reply.profilePicture)}</div>
          <div><p className="text-white text-xs font-medium">{reply.userName}</p><p className="text-slate-500 text-xs">{formatDate ? formatDate(reply.createdAt) : reply.createdAt}</p></div>
        </div>
        {reply.userId === currentUser.userId && <button onClick={() => onDeleteComment(reply.commentId)} className="p-0.5 text-slate-500 hover:text-red-400 rounded"><Trash2 size={11} /></button>}
      </div>
      <p className="text-slate-300 text-xs ml-8 mb-1">{reply.content}</p>
      {reply.reactionSummary?.totalReactions > 0 && <div className="mb-1 ml-8 flex items-center gap-1"><div className="flex -space-x-1">{Object.entries(reply.reactionSummary.reactionCounts || {}).map(([type, count]) => { const R = REACTION_TYPES[type]?.icon; return R ? <div key={type} className={`w-4 h-4 rounded-full bg-slate-800 border border-slate-900 flex items-center justify-center ${REACTION_TYPES[type].color}`}><R size={7} /></div> : null; })}</div><span className="text-slate-400 text-xs">{reply.reactionSummary.totalReactions}</span></div>}
      <div className="ml-8 relative inline-block">
        <button onClick={() => setShowReactions(!showReactions)} className={`text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 ${reply.reactionSummary?.userReaction ? REACTION_TYPES[reply.reactionSummary.userReaction]?.color : ''}`}>
          {reply.reactionSummary?.userReaction ? (() => { const R = REACTION_TYPES[reply.reactionSummary.userReaction]?.icon; return R ? <><R size={10} />{reply.reactionSummary.userReaction}</> : <><ThumbsUp size={10} />React</>; })() : <><ThumbsUp size={10} />React</>}
        </button>
        {showReactions && (<><div className="fixed inset-0 z-10" onClick={() => setShowReactions(false)} /><div className="absolute bottom-full left-0 mb-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1 flex gap-0.5 z-20">{Object.entries(REACTION_TYPES).map(([type, { icon: Icon, color, hoverColor }]) => (<button key={type} onClick={() => { onCommentReaction(reply.commentId, type); setShowReactions(false); }} className={`p-1 rounded-lg ${color} ${hoverColor} ${reply.reactionSummary?.userReaction === type ? 'bg-slate-700' : ''}`}><Icon size={11} /></button>))}</div></>)}
      </div>
    </div>
  );
}